import React from 'react';
import { Typography } from '@mui/material';
import LegalPageLayout from './LegalPageLayout';
import { useTenantConfig } from '../../contexts/TenantConfigContext';

const RefundPolicyPage: React.FC = () => {
  const { config } = useTenantConfig();
  const brandName = config?.branding?.displayName || 'AI-SCRIBE';
  const contactEmail = config?.accountManager?.email || 'support@ai-scribe.net';

  return (
    <LegalPageLayout title="Refund Policy" lastUpdated="17 March 2026">
      <Typography component="h2">1. Overview</Typography>
      <Typography component="p">
        {brandName} subscriptions are billed monthly through Paddle.com Market Ltd
        (&quot;Paddle&quot;), our Merchant of Record. This refund policy outlines the circumstances
        under which refunds may be issued.
      </Typography>

      <Typography component="h2">2. Cancellation</Typography>
      <Typography component="p">
        You may cancel your subscription at any time from your account settings. When you cancel:
      </Typography>
      <ul>
        <li>
          Your subscription remains active until the end of the current billing period;
        </li>
        <li>
          You will not be charged for the next billing period;
        </li>
        <li>
          No partial refund is issued for the remaining days of the current period.
        </li>
      </ul>

      <Typography component="h2">3. Refund Eligibility</Typography>
      <Typography component="p">
        We offer refunds in the following circumstances:
      </Typography>
      <ul>
        <li>
          <strong>First-time subscribers</strong>: If you are unhappy with the Service, you may
          request a full refund within <strong>7 days</strong> of your first subscription payment.
          No questions asked.
        </li>
        <li>
          <strong>Service outages</strong>: If the Service experiences a significant outage lasting
          more than 24 continuous hours during your billing period, you may request a pro-rated
          refund for the affected period.
        </li>
        <li>
          <strong>Billing errors</strong>: If you were charged incorrectly (e.g., duplicate charge,
          wrong amount), we will issue a full refund for the erroneous charge.
        </li>
      </ul>

      <Typography component="h2">4. Non-Refundable Situations</Typography>
      <Typography component="p">
        Refunds are generally not provided in the following cases:
      </Typography>
      <ul>
        <li>You simply forgot to cancel before the renewal date;</li>
        <li>You did not use the Service during the billing period;</li>
        <li>
          Your account was suspended or terminated due to a violation of our Terms of Service;
        </li>
        <li>Requests made after the 7-day window for first-time subscribers.</li>
      </ul>

      <Typography component="h2">5. How to Request a Refund</Typography>
      <Typography component="p">
        To request a refund, email us at{' '}
        <a href={`mailto:${contactEmail}`}>{contactEmail}</a> with the following information:
      </Typography>
      <ul>
        <li>Your account email address;</li>
        <li>The date of the charge;</li>
        <li>The reason for your refund request.</li>
      </ul>
      <Typography component="p">
        We will review your request and respond within 5 business days. Approved refunds are
        processed through Paddle and typically appear on your statement within 5–10 business days,
        depending on your payment provider.
      </Typography>

      <Typography component="h2">6. Paddle as Merchant of Record</Typography>
      <Typography component="p">
        All payments and refunds are processed by Paddle.com Market Ltd, which acts as the Merchant
        of Record for {brandName}. The charge on your bank or card statement will appear from
        Paddle. For payment-related queries, you may also contact Paddle directly, although we
        recommend reaching out to us first at{' '}
        <a href={`mailto:${contactEmail}`}>{contactEmail}</a>.
      </Typography>

      <Typography component="h2">7. Changes to This Policy</Typography>
      <Typography component="p">
        We may update this Refund Policy from time to time. Any changes will be posted on this page
        with an updated &quot;Last updated&quot; date. Continued use of the Service after changes
        constitutes acceptance of the updated policy.
      </Typography>
    </LegalPageLayout>
  );
};

export default RefundPolicyPage;
